/**
 * Studio Ode — Full-Width Gallery Slider
 *
 * - Drag-to-scroll on the track (mouse + touch via native overflow).
 * - Optional auto-scroll: advances by one slide every N seconds, paused
 *   on hover/focus/touch and when the tab is hidden.
 * - Desktop arrows scroll by one slide width + gap.
 * - Optional lightbox: click any image to open a full-screen overlay
 *   with prev/next/close. Keyboard: ← → Esc.
 */
(function () {
  'use strict';

  function init() {
    document.querySelectorAll('.ode-gs').forEach(function (el) {
      new GallerySlider(el);
    });
  }

  function GallerySlider(el) {
    this.el = el;
    this.track = el.querySelector('.ode-gs__track');
    this.slides = Array.prototype.slice.call(el.querySelectorAll('.ode-gs__slide'));
    if (!this.track || this.slides.length === 0) return;

    this.autoScroll = el.dataset.autoScroll === 'true';
    this.scrollSpeed = parseInt(el.dataset.scrollSpeed, 10) || 5;
    this.lightboxEnabled = el.dataset.lightbox === 'true';

    this.bindArrows();
    this.bindDrag();
    if (this.autoScroll) this.startAuto();
    if (this.lightboxEnabled) this.bindLightbox();
  }

  /* ── Arrows ───────────────────────────────────────────────── */
  GallerySlider.prototype.bindArrows = function () {
    var self = this;
    var prev = this.el.querySelector('.ode-gs__nav--prev');
    var next = this.el.querySelector('.ode-gs__nav--next');
    if (prev) prev.addEventListener('click', function () { self.scrollByOne(-1); });
    if (next) next.addEventListener('click', function () { self.scrollByOne(1); });
  };

  GallerySlider.prototype.scrollByOne = function (dir) {
    var first = this.slides[0];
    if (!first) return;
    var step = first.getBoundingClientRect().width;
    this.track.scrollBy({ left: dir * step, behavior: 'smooth' });
  };

  /* ── Drag-to-scroll (desktop mouse) ───────────────────────── */
  GallerySlider.prototype.bindDrag = function () {
    var self = this;
    var isDown = false;
    var startX = 0;
    var startScroll = 0;
    var moved = false;

    this.track.addEventListener('mousedown', function (e) {
      isDown = true;
      moved = false;
      startX = e.pageX;
      startScroll = self.track.scrollLeft;
      self.track.classList.add('is-dragging');
    });

    window.addEventListener('mouseup', function () {
      if (!isDown) return;
      isDown = false;
      self.track.classList.remove('is-dragging');
    });

    this.track.addEventListener('mouseleave', function () {
      if (!isDown) return;
      isDown = false;
      self.track.classList.remove('is-dragging');
    });

    this.track.addEventListener('mousemove', function (e) {
      if (!isDown) return;
      e.preventDefault();
      var dx = e.pageX - startX;
      if (Math.abs(dx) > 5) moved = true;
      self.track.scrollLeft = startScroll - dx;
    });

    /* Suppress click events that originated from a drag, so the
       lightbox doesn't open when the user merely panned. */
    this.track.addEventListener('click', function (e) {
      if (moved) {
        e.stopPropagation();
        e.preventDefault();
        moved = false;
      }
    }, true);
  };

  /* ── Auto-scroll ──────────────────────────────────────────── */
  GallerySlider.prototype.startAuto = function () {
    var self = this;
    var paused = false;

    var pause = function () { paused = true; };
    var resume = function () { paused = false; };

    this.el.addEventListener('mouseenter', pause);
    this.el.addEventListener('mouseleave', resume);
    this.el.addEventListener('focusin', pause);
    this.el.addEventListener('focusout', resume);
    this.el.addEventListener('touchstart', pause, { passive: true });

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) pause(); else resume();
    });

    this.autoTimer = setInterval(function () {
      if (paused) return;
      /* Wrap around when within one slide-width of the end. */
      var first = self.slides[0];
      var step = first ? first.getBoundingClientRect().width : 0;
      var maxScroll = self.track.scrollWidth - self.track.clientWidth;
      if (self.track.scrollLeft >= maxScroll - step / 2) {
        self.track.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        self.scrollByOne(1);
      }
    }, this.scrollSpeed * 1000);
  };

  /* ── Lightbox ─────────────────────────────────────────────── */
  GallerySlider.prototype.bindLightbox = function () {
    var self = this;
    this.lbIndex = 0;

    /* Build full-image list once from the slide images' data-full src */
    this.fullSrcs = this.slides.map(function (s) {
      var img = s.querySelector('img');
      return img ? (img.dataset.full || img.src) : '';
    });
    this.alts = this.slides.map(function (s) {
      var img = s.querySelector('img');
      return img ? img.alt || '' : '';
    });

    this.slides.forEach(function (slide, i) {
      slide.addEventListener('click', function () {
        self.openLightbox(i);
      });
    });

    /* Build a single shared lightbox per page if it doesn't exist */
    var lb = document.getElementById('ode-gs-lightbox');
    if (!lb) {
      lb = document.createElement('div');
      lb.id = 'ode-gs-lightbox';
      lb.className = 'ode-gs-lightbox';
      lb.setAttribute('role', 'dialog');
      lb.setAttribute('aria-modal', 'true');
      lb.setAttribute('aria-label', 'Image gallery');
      lb.innerHTML =
        '<button class="ode-gs-lightbox__close" aria-label="Close">&times;</button>' +
        '<button class="ode-gs-lightbox__nav ode-gs-lightbox__nav--prev" aria-label="Previous"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><polyline points="15 6 9 12 15 18"/></svg></button>' +
        '<img class="ode-gs-lightbox__img" alt="">' +
        '<button class="ode-gs-lightbox__nav ode-gs-lightbox__nav--next" aria-label="Next"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><polyline points="9 6 15 12 9 18"/></svg></button>';
      document.body.appendChild(lb);
    }
    this.lb = lb;
    this.lbImg = lb.querySelector('.ode-gs-lightbox__img');

    lb.querySelector('.ode-gs-lightbox__close').addEventListener('click', function () { self.closeLightbox(); });
    lb.querySelector('.ode-gs-lightbox__nav--prev').addEventListener('click', function (e) {
      e.stopPropagation();
      self.lbStep(-1);
    });
    lb.querySelector('.ode-gs-lightbox__nav--next').addEventListener('click', function (e) {
      e.stopPropagation();
      self.lbStep(1);
    });
    lb.addEventListener('click', function (e) {
      if (e.target === lb) self.closeLightbox();
    });

    document.addEventListener('keydown', function (e) {
      if (!lb.classList.contains('is-open')) return;
      if (e.key === 'Escape') self.closeLightbox();
      else if (e.key === 'ArrowLeft') self.lbStep(-1);
      else if (e.key === 'ArrowRight') self.lbStep(1);
    });
  };

  GallerySlider.prototype.openLightbox = function (i) {
    this.lbIndex = i;
    this.lbImg.src = this.fullSrcs[i];
    this.lbImg.alt = this.alts[i];
    this.lb.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  };

  GallerySlider.prototype.closeLightbox = function () {
    this.lb.classList.remove('is-open');
    document.body.style.overflow = '';
  };

  GallerySlider.prototype.lbStep = function (dir) {
    var n = this.fullSrcs.length;
    this.lbIndex = (this.lbIndex + dir + n) % n;
    this.lbImg.src = this.fullSrcs[this.lbIndex];
    this.lbImg.alt = this.alts[this.lbIndex];
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Re-init in Theme Editor */
  document.addEventListener('shopify:section:load', function (e) {
    var sec = e.target.querySelector('.ode-gs');
    if (sec) new GallerySlider(sec);
  });
})();
